import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  login as authLogin,
  logout as authLogout,
  getCurrentUser,
  getStoredUser,
  isAuthenticated,
  onAuthStateChange
} from '../api/auth/client';

// Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);
        const storedUser = getStoredUser();

        if (storedUser) {
          setUser(storedUser);
          // Verify token in background
          const freshUser = await getCurrentUser();
          if (freshUser) {
            setUser(freshUser);
          } else {
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Auth init error:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth state changes
    const unsubscribe = onAuthStateChange((newUser) => {
      setUser(newUser);
    });

    return unsubscribe;
  }, []);

  // Login handler
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await authLogin(email, password);
      setUser(result.user);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout handler
  const logout = useCallback(async (logoutAll = false) => {
    try {
      setLoading(true);
      await authLogout(logoutAll);
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const freshUser = await getCurrentUser();
      if (freshUser) {
        setUser(freshUser);
      }
      return freshUser;
    } catch (err) {
      console.error('Refresh user error:', err);
      return null;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook for protected routes
export function useRequireAuth(redirectTo = '/admin/login') {
  const { user, loading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      window.location.href = redirectTo;
    }
  }, [loading, isAuthenticated, redirectTo]);

  return { user, loading, isAuthenticated };
}

// Hook to check specific roles
export function useRequireRole(allowedRoles, redirectTo = '/admin') {
  const { user, loading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        window.location.href = '/admin/login';
      } else if (user && !allowedRoles.includes(user.role)) {
        window.location.href = redirectTo;
      }
    }
  }, [loading, isAuthenticated, user, allowedRoles, redirectTo]);

  return {
    user,
    loading,
    isAuthenticated,
    hasRole: user && allowedRoles.includes(user.role),
  };
}

export default useAuth;
